<section
  class="font-lato mx-auto w-full max-w-360 items-center overflow-x-hidden p-6 2xl:px-0"
  aria-labelledby="page-title"
>
  <div class="grid min-w-0 gap-3 overflow-hidden pb-4" id="charts-dashboard">
    <!-- Header -->
    <div
      class="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between"
    >
      <div>
        <a
          routerLink="/tickets"
          class="mb-1 inline-flex items-center gap-1 text-xs font-semibold tracking-widest text-blue-700 uppercase"
          aria-label="Back to Tickets"
        >
          <span
            class="material-icons-outlined text-[12px] leading-none"
            aria-hidden="true"
            >arrow_back</span
          >
          Tickets
        </a>
        <h1 id="page-title" class="m-0 text-3xl leading-tight font-bold">
          Ticket Insights
        </h1>
        <p class="mt-1 text-sm text-gray-400">
          Live overview of ticket volumes, satisfaction scores, and resolution
          times.
        </p>
      </div>
      <button
        class="btn-secondary flex items-center gap-1.5"
        (click)="downloadFilesAsPdf()"
        [disabled]="charts$._loadingCharts$() || isDownloading()"
        [attr.aria-label]="
          isDownloading()
            ? 'Exporting dashboard as PDF'
            : 'Export dashboard as PDF'
        "
      >
        @if (isDownloading()) {
          <mat-spinner diameter="14" aria-hidden="true" />
          <span>Exporting...</span>
        } @else {
          <span
            class="material-icons-outlined text-[16px] leading-none"
            aria-hidden="true"
            >file_download</span
          >
          <span>Export PDF</span>
        }
      </button>
    </div>

    <!-- KPI row -->
    <div
      class="bg-gray-150 grid w-full min-w-0 grid-cols-2 gap-px overflow-hidden shadow-sm lg:grid-cols-4"
      role="region"
      aria-labelledby="kpi-heading"
    >
      <h2 id="kpi-heading" class="sr-only">Key performance indicators</h2>
      <ng-container *ngxPermissionsOnly="Permission.SERVICES">
        @if (charts$.activeServices.signal(); as chart) {
          <div class="flex min-w-0 flex-col bg-white px-4 py-3 lg:px-5 lg:py-5">
            <h3
              class="m-0 flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-bold tracking-wider uppercase"
            >
              <span
                class="material-icons-outlined text-[15px]"
                aria-hidden="true"
                >lan</span
              >
              {{ chart.data.title }}
            </h3>
            @if (chart.loading) {
              <div
                class="mt-2 h-9 w-12 animate-pulse rounded bg-gray-100 lg:h-12 lg:w-16"
                aria-hidden="true"
              ></div>
            } @else {
              <div
                class="font-lato mt-1 text-[36px] leading-tight font-normal text-black lg:mt-2 lg:text-[52px]"
                [ngClass]="chart.ngClass"
                [attr.aria-label]="chart.data.title + ': ' + chart.data.value"
              >
                {{ chart.data.value }}
              </div>
              <p class="mt-0.5 text-xs text-gray-400">
                {{
                  chart.data.value > 0
                    ? 'All operational'
                    : 'No active services'
                }}
              </p>
            }
            @if (chart.data.linkEnabled) {
              <a
                class="mt-2 text-xs font-bold tracking-wider text-blue-700 uppercase hover:underline lg:mt-3"
                [routerLink]="chart.data.hyperlink || null"
                [queryParams]="chart.data.linkParams"
                [attr.aria-label]="'View all ' + chart.data.title"
              >
                View all <span aria-hidden="true">↗</span>
              </a>
            }
          </div>
        }
      </ng-container>

      <ng-container *ngxPermissionsOnly="Permission.ORDERS">
        @if (charts$.inflightOrders.signal(); as chart) {
          <div class="flex min-w-0 flex-col bg-white px-4 py-3 lg:px-5 lg:py-5">
            <h3
              class="m-0 flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-bold tracking-wider uppercase"
            >
              <span
                class="material-icons-outlined text-[15px]"
                aria-hidden="true"
                >local_shipping</span
              >
              {{ chart.data.title }}
            </h3>
            @if (chart.loading) {
              <div
                class="mt-2 h-9 w-12 animate-pulse rounded bg-gray-100 lg:h-12 lg:w-16"
                aria-hidden="true"
              ></div>
            } @else {
              <div
                class="font-lato mt-1 text-[36px] leading-tight font-normal text-black lg:mt-2 lg:text-[52px]"
                [ngClass]="chart.ngClass"
                [attr.aria-label]="chart.data.title + ': ' + chart.data.value"
              >
                {{ chart.data.value }}
              </div>
              <p class="mt-0.5 text-xs text-gray-400">
                {{ chart.data.value > 0 ? 'In progress' : 'Queue is clear' }}
              </p>
            }
            @if (chart.data.linkEnabled) {
              <a
                class="mt-2 text-xs font-bold tracking-wider text-blue-700 uppercase hover:underline lg:mt-3"
                [routerLink]="chart.data.hyperlink || null"
                [queryParams]="chart.data.linkParams"
                [attr.aria-label]="'View all ' + chart.data.title"
              >
                View all <span aria-hidden="true">↗</span>
              </a>
            }
          </div>
        }
      </ng-container>

      @if (charts$.emergencyCases.signal(); as chart) {
        <div class="flex min-w-0 flex-col bg-white px-4 py-3 lg:px-5 lg:py-5">
          <h3
            class="m-0 flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-bold tracking-wider uppercase"
          >
            <span class="material-icons-outlined text-[15px]" aria-hidden="true"
              >emergency</span
            >
            {{ chart.data.title }}
          </h3>
          @if (chart.loading) {
            <div
              class="mt-2 h-9 w-12 animate-pulse rounded bg-gray-100 lg:h-12 lg:w-16"
              aria-hidden="true"
            ></div>
          } @else {
            <div
              class="font-lato mt-1 text-[36px] leading-tight font-normal text-black lg:mt-2 lg:text-[52px]"
              [ngClass]="chart.ngClass"
              [attr.aria-label]="chart.data.title + ': ' + chart.data.value"
            >
              {{ chart.data.value }}
            </div>
            <p class="mt-0.5 text-xs text-gray-400">
              {{
                chart.data.value > 0
                  ? 'Needs immediate attention'
                  : 'None active'
              }}
            </p>
          }
          @if (chart.data.linkEnabled) {
            <a
              class="mt-2 text-xs font-bold tracking-wider text-blue-700 uppercase hover:underline lg:mt-3"
              [routerLink]="chart.data.hyperlink || null"
              [queryParams]="chart.data.linkParams"
              [attr.aria-label]="'View all ' + chart.data.title"
            >
              View all <span aria-hidden="true">↗</span>
            </a>
          }
        </div>
      }

      @if (charts$.openMaintenanceCases.signal(); as chart) {
        <div
          class="flex min-w-0 flex-col bg-white py-3 pr-1 pl-4 lg:py-5 lg:pl-5"
        >
          <h3
            class="m-0 flex min-w-0 flex-wrap items-center gap-1 text-xs font-bold tracking-wider uppercase"
          >
            <span class="material-icons-outlined text-[14px]" aria-hidden="true"
              >build</span
            >
            {{ chart.data.title }}
          </h3>
          @if (chart.loading) {
            <div
              class="mt-2 h-9 w-12 animate-pulse rounded bg-gray-100 lg:h-12 lg:w-16"
              aria-hidden="true"
            ></div>
          } @else {
            <div
              class="font-lato mt-1 text-[36px] leading-tight font-normal text-black lg:mt-2 lg:text-[52px]"
              [ngClass]="chart.ngClass"
              [attr.aria-label]="chart.data.title + ': ' + chart.data.value"
            >
              {{ chart.data.value }}
            </div>
            <p class="mt-0.5 text-xs text-gray-400">
              {{ chart.data.value > 0 ? 'Scheduled' : 'None scheduled' }}
            </p>
          }
          @if (chart.data.linkEnabled) {
            <a
              class="mt-2 text-xs font-bold tracking-wider text-blue-700 uppercase hover:underline lg:mt-3"
              [routerLink]="chart.data.hyperlink || null"
              [queryParams]="chart.data.linkParams"
              [attr.aria-label]="'View all ' + chart.data.title"
            >
              View all <span aria-hidden="true">↗</span>
            </a>
          }
        </div>
      }
    </div>

    <!-- Gauges: Customer satisfaction | Resolution time -->
    <div class="grid gap-3 lg:grid-cols-2">
      <!-- Customer satisfaction card -->
      <div
        class="bg-white p-4 shadow-sm"
        role="region"
        aria-labelledby="csat-heading"
      >
        <h2
          id="csat-heading"
          class="font-montserrat m-0 flex items-center gap-1.5 text-base tracking-wider uppercase"
        >
          <span class="material-icons-outlined text-[20px]" aria-hidden="true"
            >sentiment_satisfied_alt</span
          >
          Customer satisfaction
        </h2>
        <div
          class="flex flex-col items-center divide-y divide-gray-100 sm:flex-row sm:items-stretch sm:divide-x sm:divide-y-0"
        >
          @if (charts$.yearNitelNps.signal(); as chart) {
            <div
              class="flex w-full flex-col items-center py-4 sm:flex-1 sm:pr-2 sm:pl-4 [&_canvas]:!overflow-visible [&_ngx-gauge]:overflow-visible! [&_ngx-gauge_.reading-block]:font-semibold [&_svg]:!overflow-visible [&_svg_text]:font-[inherit] [&_svg_text]:!text-[8px]"
            >
              @if (chart.loading) {
                <div
                  class="h-[140px] w-[140px] animate-pulse rounded-full bg-gray-100"
                  aria-hidden="true"
                ></div>
              } @else if (chart.data.records === 0) {
                <h3
                  class="font-lato m-0 self-start text-xs font-normal tracking-normal text-gray-400 normal-case"
                >
                  {{ chart.data.title }}
                </h3>
                <div
                  class="flex h-[200px] flex-col items-center justify-center gap-1.5"
                  role="status"
                >
                  <span
                    class="material-icons-outlined text-[2rem] text-gray-300"
                    aria-hidden="true"
                    >bar_chart</span
                  >
                  <span class="text-sm font-semibold text-gray-400"
                    >No data available</span
                  >
                  <span class="text-xs text-gray-300"
                    >Nothing to display for this period</span
                  >
                </div>
              } @else {
                <ngx-gauge
                  [value]="chart.data.value"
                  [min]="chart.data.minValue"
                  [max]="chart.data.maxValue"
                  [type]="'arch'"
                  [cap]="'butt'"
                  [thick]="13"
                  [size]="200"
                  [margin]="35"
                  [thresholds]="chart.data.thresholds"
                  [style.color]="
                    getThresholdColor(chart.data.thresholds, chart.data.value)
                  "
                  [label]="''"
                  [markers]="chart.data.markers"
                  [attr.aria-label]="
                    'NPS score: ' + chart.data.value + ' out of 100'
                  "
                ></ngx-gauge>
                <div class="-mt-6 font-semibold" aria-hidden="true">NPS</div>
                <div class="text-sm text-gray-400" aria-hidden="true">
                  {{ chart.data.value }} / 100
                </div>
              }
            </div>
          }

          @if (charts$.csatSurveyResults.signal(); as chart) {
            <div
              class="flex w-full flex-col items-center py-4 sm:flex-1 sm:pr-4 sm:pl-2 [&_canvas]:overflow-visible! [&_ngx-gauge]:overflow-visible! [&_ngx-gauge_.reading-block]:font-semibold [&_svg]:overflow-visible! [&_svg_text]:font-[inherit] [&_svg_text]:text-[8px]!"
            >
              @if (chart.loading) {
                <div
                  class="h-35 w-35 animate-pulse rounded-full bg-gray-100"
                  aria-hidden="true"
                ></div>
              } @else if (chart.data.records === 0) {
                <h3
                  class="font-lato m-0 self-start text-xs font-normal tracking-normal text-gray-400 normal-case"
                >
                  {{ chart.data.title }}
                </h3>
                <div
                  class="flex h-50 flex-col items-center justify-center gap-1.5"
                  role="status"
                >
                  <span
                    class="material-icons-outlined text-[2rem] text-gray-300"
                    aria-hidden="true"
                    >bar_chart</span
                  >
                  <span class="text-sm font-semibold text-gray-400"
                    >No data available</span
                  >
                  <span class="text-xs text-gray-300"
                    >Nothing to display for this period</span
                  >
                </div>
              } @else {
                <ngx-gauge
                  [value]="chart.data.value"
                  [min]="chart.data.minValue"
                  [max]="chart.data.maxValue"
                  [type]="'arch'"
                  [cap]="'butt'"
                  [thick]="13"
                  [size]="200"
                  [margin]="35"
                  [thresholds]="chart.data.thresholds"
                  [style.color]="
                    getThresholdColor(chart.data.thresholds, chart.data.value)
                  "
                  [label]="''"
                  [markers]="chart.data.markers"
                  [attr.aria-label]="
                    'CSAT score: ' + chart.data.value + ' out of 5'
                  "
                ></ngx-gauge>
                <div class="-mt-6 font-semibold" aria-hidden="true">CSAT</div>
                <div class="text-sm text-gray-400" aria-hidden="true">
                  {{ chart.data.value }} / 5
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Resolution time card -->
      <div
        class="bg-white p-4 shadow-sm"
        role="region"
        aria-labelledby="resolution-time-heading"
      >
        <h2
          id="resolution-time-heading"
          class="font-montserrat m-0 flex items-center gap-1.5 text-base tracking-wider uppercase"
        >
          <span class="material-icons-outlined text-[20px]" aria-hidden="true"
            >schedule</span
          >
          Resolution time
        </h2>
        @if (charts$.mttrAllNOCCasesLife.signal(); as chart) {
          <div
            class="flex w-full flex-col items-center py-4 [&_canvas]:overflow-visible! [&_ngx-gauge]:overflow-visible! [&_ngx-gauge_.reading-block]:font-semibold [&_svg]:overflow-visible! [&_svg_text]:font-[inherit] [&_svg_text]:text-[8px]!"
          >
            @if (chart.loading) {
              <div
                class="h-35 w-35 animate-pulse rounded-full bg-gray-100"
                aria-hidden="true"
              ></div>
            } @else if (chart.data.records === 0) {
              <h3
                class="font-lato m-0 self-start text-xs font-normal tracking-normal text-gray-400 normal-case"
              >
                {{ chart.data.title }}
              </h3>
              <div
                class="flex h-50 flex-col items-center justify-center gap-1.5"
                role="status"
              >
                <span
                  class="material-icons-outlined text-[2rem] text-gray-300"
                  aria-hidden="true"
                  >bar_chart</span
                >
                <span class="text-sm font-semibold text-gray-400"
                  >No data available</span
                >
                <span class="text-xs text-gray-300"
                  >Nothing to display for this period</span
                >
              </div>
            } @else {
              <ngx-gauge
                [value]="chart.data.value"
                [min]="chart.data.minValue"
                [max]="chart.data.maxValue"
                [type]="'arch'"
                [cap]="'butt'"
                [thick]="13"
                [size]="200"
                [margin]="35"
                [thresholds]="chart.data.thresholds"
                [style.color]="
                  getThresholdColor(chart.data.thresholds, chart.data.value)
                "
                [label]="''"
                [markers]="chart.data.markers"
                [attr.aria-label]="
                  'Resolution time: ' + chart.data.value + ' days'
                "
              ></ngx-gauge>
              <div class="-mt-6 font-semibold" aria-hidden="true">Days</div>
              <div class="text-sm text-gray-400" aria-hidden="true">
                {{ chart.data.value }} / {{ chart.data.maxValue }}
              </div>
            }
          </div>
        }
      </div>
    </div>

    <!-- Ticket metric cards -->
    <div
      class="grid w-full grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5"
      role="region"
      aria-label="Ticket metrics"
    >
      <!-- Closed tickets (last 30d) -->
      @if (charts$.newClosedCases.signal(); as p) {
        @if (charts$.closedCases.signal(); as s) {
          <div class="flex flex-col bg-white p-4 shadow-sm">
            <h3 class="mb-1 text-xs font-semibold uppercase">Closed Tickets</h3>
            <span class="mb-3 text-[10px] text-gray-400">Last 30 days</span>
            @if (p.loading || s.loading) {
              <div
                class="mt-2 h-7 w-16 animate-pulse rounded bg-gray-100"
                aria-hidden="true"
              ></div>
              <div
                class="mt-3 h-[3px] rounded-full bg-gray-100"
                aria-hidden="true"
              ></div>
            } @else {
              <div class="mt-2 flex items-baseline justify-between gap-2">
                <span
                  class="font-lato text-[28px] leading-none font-semibold"
                  [ngClass]="p.data.value === 0 ? 'text-coral' : 'text-emerald'"
                  >{{ p.data.value }}</span
                >
                <span class="shrink-0 text-[12px] text-gray-400"
                  >{{ s.data.value }} all time</span
                >
              </div>
              <div
                class="bg-gray-150 mt-3 h-[3px] overflow-hidden rounded-full"
                role="progressbar"
                [attr.aria-valuenow]="p.data.value"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="s.data.value"
                aria-label="Closed tickets last 30 days relative to all time"
              >
                <div
                  class="bg-emerald h-full rounded-full"
                  [style.width.%]="
                    s.data.value > 0 ? (p.data.value / s.data.value) * 100 : 0
                  "
                ></div>
              </div>
              <div
                class="mt-3 flex items-center justify-between gap-2 pt-2 text-xs"
              >
                @if (p.data.linkEnabled) {
                  <a
                    class="font-medium text-blue-700 hover:underline"
                    [routerLink]="p.data.hyperlink || null"
                    [queryParams]="p.data.linkParams"
                    aria-label="View recent closed tickets"
                    >View recent <span aria-hidden="true">↗</span></a
                  >
                }
                @if (p.data.linkEnabled && s.data.linkEnabled) {
                  <span class="text-gray-300" aria-hidden="true">·</span>
                }
                @if (s.data.linkEnabled) {
                  <a
                    class="text-gray-400 hover:text-blue-700 hover:underline"
                    [routerLink]="s.data.hyperlink || null"
                    [queryParams]="s.data.linkParams"
                    aria-label="View all closed tickets"
                    >View all <span aria-hidden="true">↗</span></a
                  >
                }
              </div>
            }
          </div>
        }
      }

      <!-- Open care tickets (>30d) -->
      @if (charts$.oldOpenCareCases.signal(); as p) {
        @if (charts$.openCareCases.signal(); as s) {
          <div class="flex flex-col bg-white p-4 shadow-sm">
            <h3 class="mb-1 text-xs font-semibold uppercase">
              General Inquiry Tickets
            </h3>
            <span class="mb-3 text-[10px] text-gray-400"
              >Older than 30 days</span
            >
            @if (p.loading || s.loading) {
              <div
                class="mt-2 h-7 w-16 animate-pulse rounded bg-gray-100"
                aria-hidden="true"
              ></div>
              <div
                class="mt-3 h-[3px] rounded-full bg-gray-100"
                aria-hidden="true"
              ></div>
            } @else {
              <div class="mt-2 flex items-baseline justify-between gap-2">
                <span
                  class="font-lato text-[28px] leading-none font-semibold"
                  [ngClass]="p.ngClass"
                  >{{ p.data.value }}</span
                >
                <span class="shrink-0 text-[12px] text-gray-400"
                  >{{ s.data.value }} total</span
                >
              </div>
              <div
                class="bg-gray-150 mt-3 h-[3px] overflow-hidden rounded-full"
                role="progressbar"
                [attr.aria-valuenow]="p.data.value"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="s.data.value"
                aria-label="General inquiry tickets older than 30 days relative to total"
              >
                <div
                  class="h-full rounded-full"
                  [class.bg-orange-500]="p.data.value > 0"
                  [class.bg-gray-300]="p.data.value === 0"
                  [style.width.%]="
                    s.data.value > 0 ? (p.data.value / s.data.value) * 100 : 0
                  "
                ></div>
              </div>
              <div
                class="mt-3 flex items-center justify-between gap-2 pt-2 text-xs"
              >
                @if (p.data.linkEnabled) {
                  <a
                    class="font-medium text-blue-700 hover:underline"
                    [routerLink]="p.data.hyperlink || null"
                    [queryParams]="p.data.linkParams"
                    aria-label="View overdue general inquiry tickets"
                    >View overdue <span aria-hidden="true">↗</span></a
                  >
                }
                @if (p.data.linkEnabled && s.data.linkEnabled) {
                  <span class="text-gray-300" aria-hidden="true">·</span>
                }
                @if (s.data.linkEnabled) {
                  <a
                    class="text-gray-400 hover:text-blue-700 hover:underline"
                    [routerLink]="s.data.hyperlink || null"
                    [queryParams]="s.data.linkParams"
                    aria-label="View all general inquiry tickets"
                    >View all <span aria-hidden="true">↗</span></a
                  >
                }
              </div>
            }
          </div>
        }
      }

      <!-- Non-proactive tickets (>30d) -->
      @if (charts$.oldOpenNOCCases.signal(); as p) {
        @if (charts$.openNOCCases.signal(); as s) {
          <div class="flex flex-col bg-white p-4 shadow-sm">
            <h3 class="mb-1 text-xs font-semibold uppercase">
              Non-Proactive Tickets
            </h3>
            <span class="mb-3 text-[10px] text-gray-400"
              >Older than 30 days</span
            >
            @if (p.loading || s.loading) {
              <div
                class="mt-2 h-7 w-16 animate-pulse rounded bg-gray-100"
                aria-hidden="true"
              ></div>
              <div
                class="mt-3 h-[3px] rounded-full bg-gray-100"
                aria-hidden="true"
              ></div>
            } @else {
              <div class="mt-2 flex items-baseline justify-between gap-2">
                <span
                  class="font-lato text-[28px] leading-none font-semibold"
                  [ngClass]="p.ngClass"
                  >{{ p.data.value }}</span
                >
                <span class="shrink-0 text-[12px] text-gray-400"
                  >{{ s.data.value }} total</span
                >
              </div>
              <div
                class="bg-gray-150 mt-3 h-[3px] overflow-hidden rounded-full"
                role="progressbar"
                [attr.aria-valuenow]="p.data.value"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="s.data.value"
                aria-label="Non-proactive tickets older than 30 days relative to total"
              >
                <div
                  class="h-full rounded-full"
                  [class.bg-orange-500]="p.data.value > 0"
                  [class.bg-gray-300]="p.data.value === 0"
                  [style.width.%]="
                    s.data.value > 0 ? (p.data.value / s.data.value) * 100 : 0
                  "
                ></div>
              </div>
              <div
                class="mt-3 flex items-center justify-between gap-2 pt-2 text-xs"
              >
                @if (p.data.linkEnabled) {
                  <a
                    class="font-medium text-blue-700 hover:underline"
                    [routerLink]="p.data.hyperlink || null"
                    [queryParams]="p.data.linkParams"
                    aria-label="View overdue non-proactive tickets"
                    >View overdue <span aria-hidden="true">↗</span></a
                  >
                }
                @if (p.data.linkEnabled && s.data.linkEnabled) {
                  <span class="text-gray-300" aria-hidden="true">·</span>
                }
                @if (s.data.linkEnabled) {
                  <a
                    class="text-gray-400 hover:text-blue-700 hover:underline"
                    [routerLink]="s.data.hyperlink || null"
                    [queryParams]="s.data.linkParams"
                    aria-label="View all non-proactive tickets"
                    >View all <span aria-hidden="true">↗</span></a
                  >
                }
              </div>
            }
          </div>
        }
      }

      <!-- Open support tickets (>30d) -->
      @if (charts$.oldOpenSupportCases.signal(); as p) {
        @if (charts$.openSuportCases.signal(); as s) {
          <div class="flex flex-col bg-white p-4 shadow-sm">
            <h3 class="mb-1 text-xs font-semibold uppercase">
              Support (NOC) Tickets
            </h3>
            <span class="mb-3 text-[10px] text-gray-400"
              >Older than 30 days</span
            >
            @if (p.loading || s.loading) {
              <div
                class="mt-2 h-7 w-16 animate-pulse rounded bg-gray-100"
                aria-hidden="true"
              ></div>
              <div
                class="mt-3 h-0.75 rounded-full bg-gray-100"
                aria-hidden="true"
              ></div>
            } @else {
              <div class="mt-2 flex items-baseline justify-between gap-2">
                <span
                  class="font-lato text-[28px] leading-none font-semibold"
                  [ngClass]="p.ngClass"
                  >{{ p.data.value }}</span
                >
                <span class="shrink-0 text-[12px] text-gray-400"
                  >{{ s.data.value }} total</span
                >
              </div>
              <div
                class="bg-gray-150 mt-3 h-[3px] overflow-hidden rounded-full"
                role="progressbar"
                [attr.aria-valuenow]="p.data.value"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="s.data.value"
                aria-label="Support NOC tickets older than 30 days relative to total"
              >
                <div
                  class="h-full rounded-full"
                  [class.bg-orange-500]="p.data.value > 0"
                  [class.bg-gray-300]="p.data.value === 0"
                  [style.width.%]="
                    s.data.value > 0 ? (p.data.value / s.data.value) * 100 : 0
                  "
                ></div>
              </div>
              <div
                class="mt-3 flex items-center justify-between gap-2 pt-2 text-xs"
              >
                @if (p.data.linkEnabled) {
                  <a
                    class="font-medium text-blue-700 hover:underline"
                    [routerLink]="p.data.hyperlink || null"
                    [queryParams]="p.data.linkParams"
                    aria-label="View overdue support NOC tickets"
                    >View overdue <span aria-hidden="true">↗</span></a
                  >
                }
                @if (p.data.linkEnabled && s.data.linkEnabled) {
                  <span class="text-gray-300" aria-hidden="true">·</span>
                }
                @if (s.data.linkEnabled) {
                  <a
                    class="text-gray-400 hover:text-blue-700 hover:underline"
                    [routerLink]="s.data.hyperlink || null"
                    [queryParams]="s.data.linkParams"
                    aria-label="View all support NOC tickets"
                    >View all <span aria-hidden="true">↗</span></a
                  >
                }
              </div>
            }
          </div>
        }
      }

      <!-- Billing & payment tickets (>30d) -->
      @if (charts$.oldOpenFinanceCases.signal(); as p) {
        @if (charts$.openFinanceCases.signal(); as s) {
          <div class="flex flex-col bg-white p-4 shadow-sm">
            <h3 class="mb-1 text-xs font-semibold uppercase">
              Billing &amp; Payment Tickets
            </h3>
            <span class="mb-3 text-[10px] text-gray-400"
              >Older than 30 days</span
            >
            @if (p.loading || s.loading) {
              <div
                class="mt-2 h-7 w-16 animate-pulse rounded bg-gray-100"
                aria-hidden="true"
              ></div>
              <div
                class="mt-3 h-0.75 rounded-full bg-gray-100"
                aria-hidden="true"
              ></div>
            } @else {
              <div class="mt-2 flex items-baseline justify-between gap-2">
                <span
                  class="font-lato text-[28px] leading-none font-semibold"
                  [ngClass]="p.ngClass"
                  >{{ p.data.value }}</span
                >
                <span class="shrink-0 text-[12px] text-gray-400"
                  >{{ s.data.value }} total</span
                >
              </div>
              <div
                class="bg-gray-150 mt-3 h-0.75 overflow-hidden rounded-full"
                role="progressbar"
                [attr.aria-valuenow]="p.data.value"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="s.data.value"
                aria-label="Billing and payment tickets older than 30 days relative to total"
              >
                <div
                  class="h-full rounded-full"
                  [class.bg-orange-500]="p.data.value > 0"
                  [class.bg-gray-300]="p.data.value === 0"
                  [style.width.%]="
                    s.data.value > 0 ? (p.data.value / s.data.value) * 100 : 0
                  "
                ></div>
              </div>
              <div
                class="mt-3 flex items-center justify-between gap-2 pt-2 text-xs"
              >
                @if (p.data.linkEnabled) {
                  <a
                    class="font-medium text-blue-700 hover:underline"
                    [routerLink]="p.data.hyperlink || null"
                    [queryParams]="p.data.linkParams"
                    aria-label="View overdue billing and payment tickets"
                    >View overdue <span aria-hidden="true">↗</span></a
                  >
                }
                @if (p.data.linkEnabled && s.data.linkEnabled) {
                  <span class="text-gray-300" aria-hidden="true">·</span>
                }
                @if (s.data.linkEnabled) {
                  <a
                    class="text-gray-400 hover:text-blue-700 hover:underline"
                    [routerLink]="s.data.hyperlink || null"
                    [queryParams]="s.data.linkParams"
                    aria-label="View all billing and payment tickets"
                    >View all <span aria-hidden="true">↗</span></a
                  >
                }
              </div>
            }
          </div>
        }
      }
    </div>

    <!-- Bottom row: Ticket history chart (2/3) | Ticket breakdown (1/3) -->
    <div class="grid w-full gap-3 lg:grid-cols-[2fr_1fr]">
      @if (charts$.caseHistoryByDate.signal(); as chart) {
        <app-line-values
          class="min-w-0"
          [data]="chart.data"
          [loading]="chart.loading"
          (navigate)="handleNavigation($event)"
        ></app-line-values>
      }
      <section
        class="text-body flex min-w-0 flex-col bg-white p-6 shadow-sm"
        aria-labelledby="ticket-breakdown-title"
      >
        <h2
          id="ticket-breakdown-title"
          class="font-montserrat m-0 inline-flex items-center gap-2 text-base font-bold uppercase"
        >
          <span
            class="material-icons-outlined h-5 w-5 shrink-0 text-[20px] leading-5"
            aria-hidden="true"
            >checklist</span
          >
          Ticket breakdown
        </h2>

        <ul
          class="mt-2 flex flex-1 flex-col justify-between"
          role="list"
          aria-label="Ticket breakdown metrics"
        >
          @if (charts$.newClosedCases.signal(); as recent) {
            @if (charts$.closedCases.signal(); as all) {
              <li
                class="border-divider grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b py-2 last:border-b-0"
              >
                <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                  <span
                    class="h-3.25 w-3.25 shrink-0 rounded-full"
                    [ngClass]="
                      recent.data.value === 0 ? 'bg-coral' : 'bg-emerald'
                    "
                    [attr.aria-label]="
                      recent.data.value === 0
                        ? 'Status: none closed'
                        : 'Status: closed'
                    "
                    role="img"
                  ></span>
                  Closed Tickets <span class="text-gray-400">(last 30d)</span>
                </span>
                @if (recent.loading || all.loading) {
                  <span
                    class="bg-loading h-6 w-13.5 animate-pulse rounded"
                    aria-label="Loading"
                  ></span>
                } @else {
                  <span
                    class="font-lato self-center leading-none font-bold whitespace-nowrap"
                    [ngClass]="
                      recent.data.value === 0 ? 'text-coral' : 'text-emerald'
                    "
                    [attr.aria-label]="
                      recent.data.value +
                      ' closed in last 30 days of ' +
                      all.data.value +
                      ' all time'
                    "
                    >{{ recent.data.value }} / {{ all.data.value }}</span
                  >
                }
              </li>
            }
          }

          @if (charts$.oldOpenCareCases.signal(); as overdue) {
            @if (charts$.openCareCases.signal(); as total) {
              <li
                class="border-divider grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b py-2 last:border-b-0"
              >
                <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                  <span
                    class="h-3.25 w-3.25 shrink-0 rounded-full"
                    [ngClass]="
                      overdue.data.value === 0 ? 'bg-emerald' : 'bg-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value === 0
                        ? 'Status: good'
                        : 'Status: overdue'
                    "
                    role="img"
                  ></span>
                  General Inquiry <span class="text-gray-400">(&gt;30d)</span>
                </span>
                @if (overdue.loading || total.loading) {
                  <span
                    class="bg-loading h-6 w-13.5 animate-pulse rounded"
                    aria-label="Loading"
                  ></span>
                } @else {
                  <span
                    class="font-lato self-center leading-none font-bold whitespace-nowrap"
                    [ngClass]="
                      overdue.data.value === 0 ? 'text-emerald' : 'text-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value +
                      ' overdue of ' +
                      total.data.value +
                      ' total general inquiry tickets'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                }
              </li>
            }
          }

          @if (charts$.oldOpenNOCCases.signal(); as overdue) {
            @if (charts$.openNOCCases.signal(); as total) {
              <li
                class="border-divider grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b py-2 last:border-b-0"
              >
                <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                  <span
                    class="h-[13px] w-[13px] shrink-0 rounded-full"
                    [ngClass]="
                      overdue.data.value === 0 ? 'bg-emerald' : 'bg-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value === 0
                        ? 'Status: good'
                        : 'Status: overdue'
                    "
                    role="img"
                  ></span>
                  Non-proactive <span class="text-gray-400">(&gt;30d)</span>
                </span>
                @if (overdue.loading || total.loading) {
                  <span
                    class="bg-loading h-6 w-[54px] animate-pulse rounded"
                    aria-label="Loading"
                  ></span>
                } @else {
                  <span
                    class="font-lato self-center leading-none font-bold whitespace-nowrap"
                    [ngClass]="
                      overdue.data.value === 0 ? 'text-emerald' : 'text-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value +
                      ' overdue of ' +
                      total.data.value +
                      ' total non-proactive tickets'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                }
              </li>
            }
          }

          @if (charts$.oldOpenSupportCases.signal(); as overdue) {
            @if (charts$.openSuportCases.signal(); as total) {
              <li
                class="border-divider grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b py-2 last:border-b-0"
              >
                <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                  <span
                    class="h-[13px] w-[13px] shrink-0 rounded-full"
                    [ngClass]="
                      overdue.data.value === 0 ? 'bg-emerald' : 'bg-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value === 0
                        ? 'Status: good'
                        : 'Status: overdue'
                    "
                    role="img"
                  ></span>
                  Support (NOC) <span class="text-gray-400">(&gt;30d)</span>
                </span>
                @if (overdue.loading || total.loading) {
                  <span
                    class="bg-loading h-6 w-[54px] animate-pulse rounded"
                    aria-label="Loading"
                  ></span>
                } @else {
                  <span
                    class="font-lato self-center leading-none font-bold whitespace-nowrap"
                    [ngClass]="
                      overdue.data.value === 0 ? 'text-emerald' : 'text-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value +
                      ' overdue of ' +
                      total.data.value +
                      ' total support NOC tickets'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                }
              </li>
            }
          }

          @if (charts$.oldOpenFinanceCases.signal(); as overdue) {
            @if (charts$.openFinanceCases.signal(); as total) {
              <li
                class="border-divider grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b py-2 last:border-b-0"
              >
                <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                  <span
                    class="h-[13px] w-[13px] shrink-0 rounded-full"
                    [ngClass]="
                      overdue.data.value === 0 ? 'bg-emerald' : 'bg-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value === 0
                        ? 'Status: good'
                        : 'Status: overdue'
                    "
                    role="img"
                  ></span>
                  Billing &amp; Payment
                  <span class="text-gray-400">(&gt;30d)</span>
                </span>
                @if (overdue.loading || total.loading) {
                  <span
                    class="bg-loading h-6 w-[54px] animate-pulse rounded"
                    aria-label="Loading"
                  ></span>
                } @else {
                  <span
                    class="font-lato self-center leading-none font-bold whitespace-nowrap"
                    [ngClass]="
                      overdue.data.value === 0 ? 'text-emerald' : 'text-coral'
                    "
                    [attr.aria-label]="
                      overdue.data.value +
                      ' overdue of ' +
                      total.data.value +
                      ' total billing and payment tickets'
                    "
                    >{{ overdue.data.value }} / {{ total.data.value }}</span
                  >
                }
              </li>
            }
          }

          @if (charts$.emergencyCases.signal(); as chart) {
            <li
              class="border-divider grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b py-2 last:border-b-0"
            >
              <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                <span
                  class="h-[13px] w-[13px] shrink-0 rounded-full"
                  [ngClass]="chart.data.value === 0 ? 'bg-emerald' : 'bg-coral'"
                  [attr.aria-label]="
                    chart.data.value === 0
                      ? 'Status: none active'
                      : 'Status: active'
                  "
                  role="img"
                ></span>
                Emergency Tickets
              </span>
              @if (chart.loading) {
                <span
                  class="bg-loading h-6 w-[54px] animate-pulse rounded"
                  aria-label="Loading"
                ></span>
              } @else {
                <span
                  class="font-lato self-center leading-none font-bold whitespace-nowrap"
                  [ngClass]="
                    chart.data.value === 0 ? 'text-emerald' : 'text-coral'
                  "
                  [attr.aria-label]="chart.data.value + ' emergency tickets'"
                  >{{ chart.data.value }}</span
                >
              }
            </li>
          }

          @if (charts$.openMaintenanceCases.signal(); as chart) {
            <li
              class="border-divider grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b py-2 last:border-b-0"
            >
              <span class="font-lato flex min-w-0 items-center gap-2 text-sm">
                <span
                  class="h-[13px] w-[13px] shrink-0 rounded-full"
                  [ngClass]="chart.data.value === 0 ? 'bg-emerald' : 'bg-coral'"
                  [attr.aria-label]="
                    chart.data.value === 0
                      ? 'Status: none scheduled'
                      : 'Status: scheduled'
                  "
                  role="img"
                ></span>
                Scheduled Maintenance Tickets
              </span>
              @if (chart.loading) {
                <span
                  class="bg-loading h-6 w-[54px] animate-pulse rounded"
                  aria-label="Loading"
                ></span>
              } @else {
                <span
                  class="font-lato self-center leading-none font-bold whitespace-nowrap"
                  [ngClass]="
                    chart.data.value === 0 ? 'text-emerald' : 'text-coral'
                  "
                  [attr.aria-label]="
                    chart.data.value + ' scheduled maintenance tickets'
                  "
                  >{{ chart.data.value }}</span
                >
              }
            </li>
          }
        </ul>
      </section>
    </div>

    <!-- YTD NOC ticket codes -->
    <div class="grid w-full items-stretch gap-3">
      @if (charts$.ytdNocCasesClosedCode.signal(); as chart) {
        <app-horizontal-bars
          class="min-w-0"
          [data]="chart.data"
          [loading]="chart.loading"
          (navigate)="handleNavigation($event)"
        ></app-horizontal-bars>
      }
    </div>
  </div>
</section>
